import {put, call} from 'redux-saga/effects';
import Invoice from "../../entities/invoice";
import * as Actions from "../actions/invoiceActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';
import FinancialHelper from '../../Financial/FinancialHelper';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type InvoiceAction = {
    invoiceId: string;
    invoice: Invoice;
    invoices: Invoice[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneInvoice(action: InvoiceAction) {
	const session = tabSession.captureGeneration();
	try {
		const invoice: Invoice = yield call(api.findOne, 'invoice', action.invoiceId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.INVOICE_FETCH_ONE_SUCCESS, invoiceId: action.invoiceId, invoice});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICE_FETCH_ONE_FAILURE, invoiceId: action.invoiceId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {InvoiceAction} action
 */
export function* saveInvoice(action: InvoiceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ INVOICE_SAVE saga', action.invoice?.id);
    
	if (action.invoice === undefined || action.invoice === null) {
		console.error('saveInvoice received a null');
		throw new Error('saveInvoice received a null');
	}

	if (!(action.invoice instanceof Invoice)) {
		action.invoice = new Invoice(action.invoice);
	}

	try {
		// OWA-860: queue the same current totals shown by the draft invoice.
		action.invoice = FinancialHelper.withCurrentDraftTotals(action.invoice);

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'invoice', action.invoice);
		if (addResult.isErr()) {
			console.error(`⮂ saveInvoice: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.INVOICE_SAVE_FAILURE, invoice: action.invoice, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.INVOICE_SAVE, invoice: action.invoice});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.INVOICE_SAVE_FAILURE, invoice: action.invoice, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {InvoiceAction} action
 * @param {Invoice[]} action.invoices
 */
export function* saveManyInvoices(action: InvoiceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ INVOICE_SAVE_MANY saga', action.invoices.map(invoice => invoice.id).join(','));
    
    if (!Array.isArray(action.invoices)) {
        console.error('saveManyInvoices expected an array, received', action.invoices);
        throw new Error('saveManyInvoices expected an array');
    }

	try {
		const toSave: Invoice[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.invoices.length; i++) {
			const invoice = FinancialHelper.withCurrentDraftTotals(action.invoices[i]);
			invoice.persisted = false;
			toSave.push(invoice);
		}

		const addResult = yield call(idb.add, 'invoice', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyInvoices: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.INVOICE_SAVE_MANY_FAILURE, invoices: action.invoices, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.INVOICE_SAVE_MANY, invoices: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.INVOICE_SAVE_MANY_FAILURE, invoices: action.invoices, status: 500, err});

		console.error(`Error saving ${action.invoices.length} invoices to database`, err);
	}
}

/**
 * @param {InvoiceAction} action
 */
export function* removeInvoice(action: InvoiceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.invoice === undefined || action.invoice === null || !(action.invoice instanceof Invoice)) {
			console.error('removeInvoice expected type Invoice, received', action.invoice);
			throw new Error('removeInvoice expected type Invoice');
		}

	try {
		// Mark as deleted
		action.invoice.status = 'D';
		action.invoice.addedOn = newHRTimestamp();
		action.invoice.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'invoice', action.invoice);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeInvoice: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICE_REMOVE_FAILURE, invoice: action.invoice, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICE_REMOVE, invoice: action.invoice});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICE_REMOVE_FAILURE, invoice: action.invoice, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Invoice.
	*
	* @param {InvoiceAction} action
	*/
export function* restoreInvoice(action: InvoiceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreInvoice expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.invoice === undefined || action.invoice === null) {
        console.error('restoreInvoice expected a Invoice, received null');
        throw new Error('restoreInvoice expected a Invoice, received null');
    }
    else if (!(action.invoice instanceof Invoice)) action.invoice = new Invoice(action.invoice); 
    
	try {
		// Mark as restored
		action.invoice.status = 'U';
		action.invoice.addedOn = newHRTimestamp();
		action.invoice.addedBy = userId;
		action.invoice.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'invoice', action.invoice);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreInvoice: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICE_RESTORE_FAILURE, invoice: action.invoice, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICE_RESTORE, invoice: action.invoice});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICE_RESTORE_FAILURE, invoice: action.invoice, status: 500, err});

		console.error('Error restoring from database', err);
	}
}